import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { FieldInputProps, FormikErrors, FormikTouched, useFormik } from 'formik'
import * as Yup from 'yup'

import { trackNewsletterFormSubmit } from 'app/components/shared/Analytics/segment'
import apiClient from 'helpers/clients/apiClient'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

export type NewsletterProps = {
  getFieldProps: (nameOrOptions: any) => FieldInputProps<any>
  touched: FormikTouched<{ email: string }>
  errors: FormikErrors<{ email: string }>
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
}

type Props = {
  pathname: string
  children(props: NewsletterProps): React.ReactElement
}

export const NewsletterLogic: React.FC<Props> = ({ children, pathname }) => {
  const { t } = useTranslation(['common', 'newsletter'])
  const dispatch = useDispatch()

  const handleSubscribe = async (email: string) => {
    const response = await apiClient.setNewsletter(email)
    if (!response.success || !response.data) {
      if (response.error?.errors?.some((s) => s.title.includes('present'))) {
        dispatch(displaySnackbar({ message: t('newsletter:alreadySaved'), isError: true }))
        resetForm()
      } else {
        dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
      }
      return
    }

    trackNewsletterFormSubmit(pathname)
    dispatch(displaySnackbar({ message: t('newsletter:success'), isError: false }))
    resetForm()
  }

  const { getFieldProps, touched, errors, handleSubmit, resetForm } = useFormik({
    initialValues: {
      email: '',
    },
    onSubmit: async ({ email }: { email: string }) => {
      await handleSubscribe(email)
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email(t('newsletter:validationError')).required(t('common:required')),
    }),
  })

  return children({
    getFieldProps,
    touched,
    errors,
    handleSubmit,
  })
}
